import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import {
    Layers,
    BrainCircuit,
    Network,
    ShieldCheck,
    Code2,
    ArrowRight,
    Blocks,
    Rocket,
    Microscope,
    ArrowRightLeft,
    Compass,
} from 'lucide-react';
import Landing from '../landingComponent/landing.js';
import myPic from '../images/stivan.jpg';

const FEATURES_LEFT = [
    { Icon: Layers, title: 'Systems Architecture', body: 'Designing platforms and services built to scale reliably under real-world load.' },
    { Icon: BrainCircuit, title: 'AI Engineering', body: 'Building practical AI-driven features and workflows that create measurable value.' },
];

const FEATURES_RIGHT = [
    { Icon: Network, title: 'Enterprise Integration', body: 'Connecting complex systems so data and processes flow cleanly end-to-end.' },
    { Icon: ShieldCheck, title: 'Reliability & Security', body: 'Production-grade code — hardened, observable, and built to be trusted.' },
];

const CAPABILITIES = [
    {
        Icon: BrainCircuit,
        title: 'AI Engineering',
        body: 'Building production LLM apps, RAG pipelines, and agentic workflows — turning AI from a demo into something teams actually trust in prod.',
        stack: ['Python', 'Claude', 'OpenAI', 'Bedrock', 'RAG', 'Vector DBs', 'Prompt Engineering', 'Claude Code', 'Cursor'],
    },
    {
        Icon: Layers,
        title: 'Systems Architecture',
        body: 'Designing distributed services and platforms that scale reliably under real-world load — and stay maintainable when the team doubles.',
        stack: ['TypeScript', 'Node.js', 'Python', 'AWS', 'Docker', 'Postgres', 'Snowflake'],
    },
    {
        Icon: Code2,
        title: 'Full-Stack Web',
        body: 'Shipping polished, performant products end-to-end — from data model to pixel, with a bias toward speed and clarity.',
        stack: ['React', 'TypeScript', 'JavaScript', 'Node.js', 'Tailwind'],
    },
    {
        Icon: Blocks,
        title: 'Enterprise Modernization',
        body: 'Bridging legacy enterprise systems into the modern stack — without breaking the business that runs on them.',
        stack: ['PowerBuilder', 'Sybase', 'Visual Basic', 'C#', 'MySQL', 'Migration'],
    },
];

const ENGAGEMENTS = [
    {
        Icon: Rocket,
        title: 'AI Feature Build-out',
        body: 'Prototype-to-production build of an AI or LLM feature — RAG, agents, or workflows — that ships and holds up once real users touch it.',
        shape: 'Project · fixed scope',
    },
    {
        Icon: Microscope,
        title: 'Architecture Review',
        body: 'A second pair of senior eyes on a system, codebase, or design doc — with a written breakdown of what to keep, cut, and fix first.',
        shape: 'One-off · written deliverable',
    },
    {
        Icon: ArrowRightLeft,
        title: 'Legacy Modernization',
        body: 'Bridging older enterprise systems — PowerBuilder, Sybase, VB — into a modern stack without breaking the business that runs on them.',
        shape: 'Milestone-based',
    },
    {
        Icon: Compass,
        title: 'Fractional Tech Lead',
        body: 'Ongoing advisory for teams without a senior IC — architecture calls, code review, hiring signal, unblocking the hard decisions.',
        shape: 'Ongoing · a few hours a week',
    },
];

function FeatureItem({ Icon, title, body }) {
    return (
        <div className="flex flex-col items-center text-center gap-3">
            <Icon className="text-accent" size={44} strokeWidth={1.5} aria-hidden="true" />
            <h3 className="text-ink-primary font-semibold text-lg">{title}</h3>
            <p className="text-ink-secondary text-sm max-w-[240px] leading-relaxed">{body}</p>
        </div>
    );
}

function SectionTitle({ children }) {
    return (
        <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-ink-primary">{children}</h2>
            <div className="mx-auto mt-3 h-1 w-12 bg-accent rounded-full"></div>
        </div>
    );
}

function CodeLine({ n, children }) {
    return (
        <div className="flex gap-4 whitespace-pre">
            <span className="text-slate-600 select-none w-4 text-right shrink-0">{n}</span>
            <span className="min-w-0">{children}</span>
        </div>
    );
}

function CodeEditorCard() {
    const kw = 'text-[#c084fc]';       // keyword (export, const, =>)
    const fn = 'text-[#21c2c8]';       // function/identifier accent
    const arg = 'text-[#93c5fd]';      // argument
    const pun = 'text-slate-400';       // punctuation
    const cm = 'text-slate-500 italic'; // comment

    return (
        <div className="w-full max-w-md mx-auto">
            <div className="rounded-2xl border border-border-subtle overflow-hidden shadow-card bg-[#0f1626]">
                {/* window chrome */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border-subtle bg-[#0b1220]">
                    <span className="w-3 h-3 rounded-full bg-[#ff5f57]" aria-hidden="true"></span>
                    <span className="w-3 h-3 rounded-full bg-[#febc2e]" aria-hidden="true"></span>
                    <span className="w-3 h-3 rounded-full bg-[#28c840]" aria-hidden="true"></span>
                    <div className="flex-1 flex justify-center">
                        <span className="font-mono text-xs text-ink-muted tracking-wide">craft.ts</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true"></span>
                        <span className="font-mono text-[10px] text-ink-muted uppercase tracking-wider">shipping</span>
                    </div>
                </div>

                {/* code body */}
                <div className="p-5 font-mono text-[13px] sm:text-sm leading-7 overflow-x-auto">
                    <CodeLine n="1"><span className={cm}>// how I build</span></CodeLine>
                    <CodeLine n="2"> </CodeLine>
                    <CodeLine n="3">
                        <span className={kw}>export</span> <span className={kw}>const</span>{' '}
                        <span className={fn}>build</span> <span className={pun}>=</span>{' '}
                        <span className={pun}>(</span><span className={arg}>idea</span><span className={pun}>)</span>{' '}
                        <span className={kw}>{'=>'}</span> <span className={arg}>idea</span>
                    </CodeLine>
                    <CodeLine n="4">
                        {'  '}<span className={pun}>.</span><span className={fn}>collaborate</span><span className={pun}>()</span>
                        {'   '}<span className={cm}>// with people</span>
                    </CodeLine>
                    <CodeLine n="5">
                        {'  '}<span className={pun}>.</span><span className={fn}>refine</span><span className={pun}>()</span>
                        {'        '}<span className={cm}>// with curiosity</span>
                    </CodeLine>
                    <CodeLine n="6">
                        {'  '}<span className={pun}>.</span><span className={fn}>ship</span><span className={pun}>();</span>
                        {'      '}<span className={cm}>// with craftsmanship</span>
                        <span className="morph-caret" aria-hidden="true">▍</span>
                    </CodeLine>
                </div>

                {/* status bar */}
                <div className="flex items-center justify-between px-4 py-2 border-t border-border-subtle bg-[#0b1220] font-mono text-[10px] text-ink-muted uppercase tracking-wider">
                    <span>TypeScript</span>
                    <span>main · UTF-8</span>
                </div>
            </div>
        </div>
    );
}

class Homepage extends Component {
    render() {
        return (
            <>
                <Landing />
                <div className="wrap">
                {/* ── Intro / Profile ─────────────────────────── */}
                <section className="py-16 sm:py-20 px-4 sm:px-6">
                    <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 items-center">
                        <div className="flex flex-col gap-10 order-2 md:order-1">
                            {FEATURES_LEFT.map((f) => <FeatureItem key={f.title} {...f} />)}
                        </div>

                        <div className="flex flex-col items-center text-center order-1 md:order-2">
                            <img
                                src={myPic}
                                alt="Stivan Kitchoukov"
                                width="240"
                                height="240"
                                className="w-56 h-56 sm:w-60 sm:h-60 rounded-full object-cover ring-4 ring-accent/30 shadow-card"
                            />
                            <h1 className="mt-6 text-3xl sm:text-4xl font-bold text-ink-primary">Stivan Kitchoukov</h1>
                            <p className="mt-1 text-accent text-sm uppercase tracking-widest">Senior Software Engineer</p>
                            <p className="mt-3 text-ink-secondary text-sm max-w-xs leading-relaxed">
                                Building AI-powered, cloud-native software at enterprise scale.
                            </p>
                        </div>

                        <div className="flex flex-col gap-10 order-3">
                            {FEATURES_RIGHT.map((f) => <FeatureItem key={f.title} {...f} />)}
                        </div>
                    </div>
                </section>

                {/* ── Intro Card ──────────────────────────────── */}
                <section className="py-16 px-4 sm:px-6 bg-bg-raised border-y border-border-subtle">
                    <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                        <div className="flex justify-center">
                            <CodeEditorCard />
                        </div>
                        <div className="bg-bg-card border border-border-subtle rounded-2xl p-8 shadow-card">
                            <h3 className="text-xl sm:text-2xl font-semibold text-ink-primary leading-snug">
                                Creating digital experiences that make life easier.
                            </h3>
                            <p className="mt-4 text-ink-secondary leading-relaxed">
                                I love building software that makes life easier. Whether it's crafting financial tools for small start-ups or complex systems for large enterprises, I design solutions that help people work smarter. Every project is shaped through collaboration, curiosity, and craftsmanship.
                            </p>
                            <Link to="/Contact" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-lg bg-accent text-white font-semibold hover:bg-accent-hover transition-colors">
                                Let's Create Together
                                <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* ── Capabilities ────────────────────────────── */}
                <section className="py-20 px-4 sm:px-6">
                    <div className="max-w-5xl mx-auto">
                        <SectionTitle>Capabilities</SectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                            {CAPABILITIES.map((c) => (
                                <div
                                    key={c.title}
                                    className="group bg-bg-card border border-border-subtle rounded-2xl p-6 sm:p-7 shadow-card hover:border-accent/60 hover:shadow-cardHover hover:-translate-y-0.5 transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-accent/10 border border-accent/25 shrink-0">
                                            <c.Icon className="text-accent" size={22} strokeWidth={1.75} aria-hidden="true" />
                                        </div>
                                        <h3 className="text-ink-primary font-semibold text-lg sm:text-xl">{c.title}</h3>
                                    </div>
                                    <p className="mt-4 text-ink-secondary text-sm leading-relaxed">{c.body}</p>
                                    <div className="mt-5 flex flex-wrap gap-1.5">
                                        {c.stack.map((t) => (
                                            <span key={t} className="cap-chip">{t}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Ways to Work Together ───────────────────── */}
                <section className="py-20 px-4 sm:px-6 bg-bg-raised border-y border-border-subtle">
                    <div className="max-w-5xl mx-auto">
                        <SectionTitle>Ways to Work Together</SectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                            {ENGAGEMENTS.map((e) => (
                                <div
                                    key={e.title}
                                    className="group flex gap-5 bg-bg-card border border-border-subtle rounded-2xl p-6 sm:p-7 shadow-card hover:border-accent/60 hover:shadow-cardHover hover:-translate-y-0.5 transition-all"
                                >
                                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 border border-accent/25 shrink-0">
                                        <e.Icon className="text-accent" size={24} strokeWidth={1.75} aria-hidden="true" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-ink-primary font-semibold text-lg">{e.title}</h3>
                                        <p className="mt-2 text-ink-secondary text-sm leading-relaxed">{e.body}</p>
                                        <p className="mt-3 text-[10px] font-mono uppercase tracking-wider text-ink-muted">{e.shape}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-10 text-center text-ink-secondary text-sm">
                            See a shape that fits?{' '}
                            <Link to="/Contact" className="text-accent hover:text-accent-hover font-medium">Let's talk →</Link>
                        </p>
                    </div>
                </section>

                </div>
            </>
        );
    }
}

export default Homepage;
